import React from "react";
import PropTypes from "prop-types";

import withStyles from "@mui/styles/withStyles";
import Button from "@mui/material/Button";
import Radio from "@mui/material/Radio";
import Switch from "@mui/material/Switch";
import Checkbox from "@mui/material/Checkbox";
import Typography from "@mui/material/Typography";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Box from "@mui/material/Box";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";

export const SquareButton = withStyles({
  root: {
    backgroundColor: "#1c355e", // unnecessarily overrides theme colors when doing branding
    borderRadius: 3,
    border: 0,
    color: "#fff",
    height: 54,
    minWidth: 54,
    width: 54,
  },
})(Button);

export const SelectionButton = withStyles({
  root: {
    color: "#1c355e", // secondary theme color applied in all cases when doing branding
    borderRadius: 0,
    width: 300,
    height: 50,
  },
})(Button);

export const ExitButton = withStyles({
  root: {
    backgroundColor: "#d1495b",
    borderRadius: 3,
    color: "#fff",
    height: 36,
    minWidth: 36,
    width: 36,
    "&:hover": {
      backgroundColor: "#b82f41",
    },
  },
})(Button);

export const GreenButton = withStyles({
  root: {
    backgroundColor: "#76bc21",
    boxShadow:
      "0px 3px 1px -2px rgb(0 0 0 / 20%), 0px 2px 2px 0px rgb(0 0 0 / 14%), 0px 1px 5px 0px rgb(0 0 0 / 12%)",
    color: "#fff",
    "&:hover": {
      backgroundColor: "#619b1b",
    },
    height: 50,
  },
})(Button);

/* TYPOGRAPHY */
export const BlueTextTypography = withStyles({
  root: {
    color: "#00798c",
    fontWeight: 600,
  },
})(Typography);

export const RedTextTypography = withStyles({
  root: {
    fontSize: 12,
    color: "#d1495b",
    fontWeight: 600,
    fontStyle: "italic",
    textAlign: "center",
  },
})(Typography);

/* RADIO, CHECKBOX, SWITCH */
export const GreenRadio = withStyles({
  root: {
    color: "#76bc21",
    "&$checked": {
      color: "#76bc21",
    },
  },
  checked: {},
})((props) => <Radio color="default" {...props} />);

export const GreenCheckbox = withStyles({
  root: {
    color: "#76bc21",
    "&$checked": {
      color: "#76bc21",
    },
  },
  checked: {},
})((props) => <Checkbox color="default" {...props} />);

export const GreenSwitch = withStyles({
  switchBase: {
    color: "#fff",
    "&$checked": {
      color: "#76bc21",
    },
    "&$checked + $track": {
      backgroundColor: "#76bc21",
    },
  },
  checked: {},
  track: {},
})(Switch);

/* TABS */
export const CustomStyledTabs = withStyles({
  indicator: { backgroundColor: "#76bc21" },
})(Tabs);
export const CustomStyledTab = withStyles((theme) => ({
  root: {
    textTransform: "none", //This removes the all caps default state
    minHeight: 90,
    borderBottom: "1px solid #c1c1c1",
    fontWeight: theme.typography.fontWeightRegular,
    "&$selected": {
      color: "#76bc21",
      fontWeight: theme.typography.fontWeightMedium,
    },
  },
  selected: {},
}))((props) => <Tab {...props} />);
export const CustomStyledTabsThin = withStyles({
  indicator: { backgroundColor: "#1c355e" },
})(Tabs);
export const CustomStyledTabThin = withStyles((theme) => ({
  root: {
    textTransform: "none", //This removes the all caps default state
    minHeight: 45,
    borderBottom: "1px solid #c1c1c1",
    backgroundColor: "#cfe8fd",
    fontWeight: theme.typography.fontWeightRegular,
    "&$selected": {
      color: "#1c355e",
      fontWeight: theme.typography.fontWeightMedium,
    },
  },
  selected: {},
}))((props) => <Tab {...props} />);
export const TabPanel = (props) => {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
};
export const a11yProps = (index) => {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
};

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.any.isRequired,
  value: PropTypes.any.isRequired,
};

/* ACCORDION */
export const CustomAccordion = withStyles({
  root: {
    border: "1px solid rgba(0, 0, 0, .125)",
    boxShadow: "none",
    "&:not(:last-child)": {
      borderBottom: 0,
    },
    "&:before": {
      display: "none",
    },
    "&$expanded": {
      margin: "auto",
    },
    "&$expanded:hover": {
      boxShadow: "0 0 0 rgba(0,0,0,0.3)",
      zIndex: 0,
    },
    "&:hover": {
      boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
      zIndex: 10,
    },
  },
  expanded: {},
  disabled: {
    "&:hover": {
      boxShadow: "none",
    },
  },
})(Accordion);
export const CustomAccordionSummary = withStyles({
  root: {
    backgroundColor: "rgba(0, 0, 0, .03)",
    borderBottom: "1px solid rgba(0, 0, 0, .125)",
    marginBottom: -1,
    height: 45,
    "&$expanded": {
      height: 45,
    },
  },
  content: {
    "&$expanded": {
      margin: "12px 0",
    },
  },
  expanded: {},
})(AccordionSummary);
export const CustomAccordionDetails = withStyles((theme) => ({
  root: {
    padding: theme.spacing(2),
  },
}))(AccordionDetails);
